import {
  ERouteLegResponseType,
  type CCLegLocation,
  type LogisticsLocationsById,
  type RouteLegResponseType,
} from '#entities';
import {
  ECaseResourceType,
  ENOPLegLocationType,
  parseItalianState,
} from '#shared';

type BuildChainsOptions = {
  requireConnectivity?: boolean; // if false -> ignore location and time when chaining
  allowOverlap?: boolean; // if false -> break chains on time overlap
  minBufferMs?: number; // require at least this buffer between legs
};

// prefer leg-level local tz fields; fall back to any location/airport tz; last resort UTC
export const resolveIanaTzFromLeg = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leg: any,
  which: 'start' | 'end',
  locationsById?: LogisticsLocationsById,
): string => {
  const fromLeg =
    which === 'start'
      ? (leg.estimatedStartTimeLocalTimezone ?? leg.departureLocalTimezone)
      : (leg.estimatedEndTimeLocalTimezone ?? leg.arrivalLocalTimezone);
  if (fromLeg) {
    return fromLeg;
  }

  if (locationsById) {
    const locRef =
      which === 'start'
        ? (leg.departureLocation ?? leg.startLocation)
        : (leg.arrivalLocation ?? leg.endLocation);
    const tz = locationsById.get(locRef?.locationId)?.location.timezone;
    if (tz) {
      return tz;
    }
  }

  const loc =
    which === 'start'
      ? (leg.startLocation ?? leg.departureLocation)
      : (leg.endLocation ?? leg.arrivalLocation);
  return (
    loc?.timezone ||
    loc?.data?.location?.timezone ||
    loc?.data?.timezone ||
    loc?.data?.airport?.timezone ||
    loc?.data?.airport?.timeZone ||
    'UTC'
  );
};

export const buildLegChains = (
  legs: RouteLegResponseType[],
  opts: BuildChainsOptions = {
    requireConnectivity: true,
    allowOverlap: false,
    minBufferMs: 0,
  },
): RouteLegResponseType[][] => {
  // SpecRef: CODE-007
  // See: docs/decisions/CODE-007-case-logistics-polymorphic-schema.md#accommodation-leg-chain-building-exclusion
  // AccommodationLegs lack departure/arrival times and locations required for chain connectivity.
  const _legs = legs.filter(
    l => l.$type !== ERouteLegResponseType.AccommodationLeg,
  );
  if (!_legs?.length) {
    return [];
  }

  const {
    requireConnectivity = true,
    allowOverlap = false,
    minBufferMs = 0,
  } = opts;

  const byId = new Map(_legs.map(l => [l.id, l]));
  const nextByPrev = new Map<string, RouteLegResponseType>();
  const indeg = new Map<string, number>(_legs.map(l => [l.id, 0]));

  for (const l of _legs) {
    const prevId = 'chainPosition' in l ? l.chainPosition.previousLegId : null;
    if (prevId && byId.has(prevId) && !nextByPrev.has(prevId)) {
      nextByPrev.set(prevId, l);
      indeg.set(l.id, (indeg.get(l.id) ?? 0) + 1);
    }
  }

  const heads = _legs.filter(l => (indeg.get(l.id) ?? 0) === 0);
  const chains: RouteLegResponseType[][] = [];

  const connected = (a: RouteLegResponseType, b: RouteLegResponseType) => {
    if (!requireConnectivity) {
      return true;
    }
    const aLocId = 'arrivalLocation' in a ? a.arrivalLocation.locationId : null;
    const bLocId =
      'departureLocation' in b ? b.departureLocation.locationId : null;
    const locOk = aLocId !== null && aLocId === bLocId;
    const aEnd = +new Date('arrivalTime' in a ? a.arrivalTime : '');
    const bStart = +new Date('departureTime' in b ? b.departureTime : '');
    const timeOk = allowOverlap
      ? bStart - aEnd >= -1
      : bStart - aEnd >= minBufferMs;
    return locOk && timeOk;
  };

  const consume = (head: RouteLegResponseType) => {
    const chain: RouteLegResponseType[] = [head];
    let cur = head;

    while (nextByPrev.has(cur.id)) {
      const nxt = nextByPrev.get(cur.id)!;
      if (!connected(cur, nxt)) {
        break;
      }
      chain.push(nxt);
      cur = nxt;
    }
    return chain;
  };

  for (const h of heads) {
    chains.push(consume(h));
  }

  // Fallback for strays/cycles
  const inChain = new Set(chains.flat().map(l => l.id));
  for (const l of _legs) {
    if (!inChain.has(l.id)) {
      chains.push([l]);
    }
  }

  // Sort chains by first leg start time
  const getLegStartTime = (l: RouteLegResponseType): string => {
    const checkInTime = 'checkInAt' in l ? l.checkInAt : '';
    return 'departureTime' in l ? l.departureTime : checkInTime;
  };

  chains.sort(
    (a, b) =>
      +new Date(getLegStartTime(a[0])) - +new Date(getLegStartTime(b[0])),
  );

  return chains;
};

// Helper to parse state based on isItalian flag
const resolveState = (
  state: string | undefined,
  isItalian: boolean,
): string | undefined => (isItalian ? parseItalianState(state ?? '') : state);

export const getMarkerName = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  legLocation: CCLegLocation | Record<string, any> | null | undefined,
  isItalian = false,
  locationsById?: LogisticsLocationsById,
): string => {
  if (!legLocation) {
    return '---';
  }

  // Resolve from logistics locations lookup when available
  const locationId = (legLocation as Record<string, string>).locationId;
  if (locationId && locationsById) {
    const entry = locationsById.get(locationId);
    if (entry) {
      const { state } = entry.location;
      const resolvedState = resolveState(state ?? undefined, isItalian);
      switch (entry.locationType) {
        case ENOPLegLocationType.Airport:
          return entry.iata || entry.icao || '---';
        case ENOPLegLocationType.Hub:
          return entry.code || '---';
        case ENOPLegLocationType.Hospital:
          return `${entry.code || 'DH'}, ${resolvedState || entry.location.country || '---'}`;
        case ENOPLegLocationType.TxCenter:
          return `${entry.code || 'TXC'}, ${resolvedState || entry.location.country || '---'}`;
        case ENOPLegLocationType.HomeResidence:
          return `${entry.location.city || '---'}, ${resolvedState || entry.location.country || '---'}`;
        case ENOPLegLocationType.Custom:
          return `${entry.location.city || '---'}, ${resolvedState || entry.location.country || '---'}`;
        default:
          return '---';
      }
    }
  }
  return '---';
};

export const getAbbreviation = (type: ECaseResourceType): string => {
  switch (type) {
    case ECaseResourceType.Surgeon:
      return 'S';
    case ECaseResourceType.ClinicalSpecialist:
      return 'CS';
    case ECaseResourceType.OCSConsole:
      return 'D';
    case ECaseResourceType.Aircraft:
    case ECaseResourceType.Crew:
    default:
      return '';
  }
};
